import { useEffect, useRef } from 'react';

/**
 * Global ref counter to track how many consumers want body overflow locked.
 * This ensures that if multiple components (e.g., mobile menu + drawer) are
 * open simultaneously, the body remains locked until ALL consumers are closed.
 */
let lockCount = 0;

/**
 * Hook to manage body overflow (prevent scrolling when modal/menu is open)
 *
 * Uses ref-counting to handle multiple concurrent consumers correctly.
 * If both mobile menu and drawer are open, closing one won't unlock
 * scrolling until both are closed.
 *
 * @param lock - Whether to lock body scroll
 */
export function useBodyOverflow(lock: boolean) {
  const hasLockedRef = useRef(false);

  useEffect(() => {
    // Only update if state changed
    if (lock && !hasLockedRef.current) {
      // Increment lock count and lock body
      lockCount++;
      document.body.style.overflow = 'hidden';
      hasLockedRef.current = true;
    } else if (!lock && hasLockedRef.current) {
      // Decrement lock count
      lockCount--;
      hasLockedRef.current = false;

      // Only unlock if no other consumers are locking
      if (lockCount === 0) {
        document.body.style.overflow = '';
      }
    }

    return () => {
      // Cleanup: if this consumer had locked, decrement and unlock if needed
      if (hasLockedRef.current) {
        lockCount--;
        if (lockCount === 0) {
          document.body.style.overflow = '';
        }
        hasLockedRef.current = false;
      }
    };
  }, [lock]);
}
